/**
 * 🏠 **The pit-house vefstofa in the world — D135, T289.**
 *
 * ## 🔴 What you see is what stops you
 *
 * The walls and ceiling are BLOCKS (`shared/src/pithouse.ts`), and this file
 * draws their faces **exactly on the block faces** — planks facing the room,
 * turf facing the weather. `06_Traps.md` records a board hung 39 cm off a wall
 * and a sheep standing in the air over a roof, both because a model's wall and
 * a block's face were two different surfaces. Here there is one.
 *
 * Only the things that sit ON blocks are models, generated from words and
 * picked by haugur (`tools/design-creature-model.mjs`, the `pit_*` entries):
 * the **roof** over the ceiling, the **oven** in its corner, the **bench** along
 * the left wall, and the **door** in the doorway — turned on its hinge when it
 * is open. A model that has not loaded, or has not been picked yet, is simply
 * not drawn; the walls are.
 *
 * ## Light — the houses' proven path, no new shader construct
 *
 * ⚠️ **A shader cannot be checked on this workstation**, so this uses only what
 * `burmesh.ts` already compiles: a `MeshLambertNodeMaterial` with a texture
 * colour and `burDaylight.mul(0.35).add(uniform(torch))` as its emissive, in
 * five torch steps. The planks inside take the light of the ROOM (so a lit oven
 * shows on them); the turf outside takes the light outside the door.
 */

import {
  Box3,
  BufferGeometry,
  DataArrayTexture,
  Float32BufferAttribute,
  Group,
  LinearMipmapLinearFilter,
  Mesh,
  NearestFilter,
  type Object3D,
  RepeatWrapping,
  SRGBColorSpace,
  type Texture,
  TextureLoader,
  Vector3,
} from 'three';
import { MeshLambertNodeMaterial } from 'three/webgpu';
import { int, texture, uniform, uv, vec3 } from 'three/tsl';
import { GLTFLoader } from 'three/examples/jsm/loaders/GLTFLoader.js';
import {
  burFrame,
  CHUNK_SIZE,
  isPitDoor,
  pitDoorHalf,
  pitDoorIsOpen,
  pitFacing,
  renderTile,
  tilePlans,
  type BurFacing,
} from '@lofi/shared';
import { burDaylight } from './burmesh.ts';

/** A pit house's door (its LOWER cell), in chunk-local cells. */
export interface PitAt {
  readonly x: number;
  readonly y: number;
  readonly z: number;
  readonly facing: BurFacing;
  readonly open: boolean;
}

/** One face of a wall or ceiling cell, on the block's own face. */
export interface PitFace {
  readonly x: number;
  readonly y: number;
  readonly z: number;
  readonly nx: number;
  readonly ny: number;
  readonly nz: number;
  readonly skin: 'planks' | 'stone' | 'roof';
}

type Kind = 'outside' | 'wall' | 'door' | 'room' | 'ceiling' | 'none';

/** What a house-local cell is — the same shape `pithouse.ts` builds. */
function kindAt(d: number, w: number, h: number): Kind {
  if (d < 0 || d > 5 || Math.abs(w) > 3) return 'outside';
  if (h < -1) return 'none';
  /* 🚪 The door is two cells: the lower at ground level, the upper in the
     ceiling row. Over the way in (d 1, w 0) the ceiling is raised one block. */
  if (d === 0 && w === 0 && (h === 0 || h === 1)) return 'door';
  const wayIn = d === 1 && w === 0;
  if (h === 1) return wayIn ? 'room' : 'ceiling';
  if (h === 2) return wayIn ? 'ceiling' : 'outside';
  if (h > 2) return 'outside';
  const ring = d === 0 || d === 5 || Math.abs(w) === 3;
  return ring ? 'wall' : 'room';
}

const STEPS: readonly (readonly [number, number, number])[] = [
  [1, 0, 0],
  [-1, 0, 0],
  [0, 1, 0],
  [0, -1, 0],
  [0, 0, 1],
  [0, 0, -1],
];

/**
 * 🧱 **Every face the walls and ceiling show**, in the door's own coordinates
 * (world or chunk-local — only offsets are added).
 *
 * | Cell | Toward | Drawn as |
 * |---|---|---|
 * | wall | the room | planks |
 * | wall | the doorway (jambs, threshold) and outside — at every height (#850) | stone |
 * | ceiling | down into the room or the doorway | planks |
 * | ceiling | outside, or up under the roof | roof — green turf, on the stone |
 *
 * Nothing between two house blocks.
 */
export function pitWallFaces(
  door: { x: number; y: number; z: number },
  facing: BurFacing,
): PitFace[] {
  const { into, right } = burFrame(facing);
  const faces: PitFace[] = [];
  for (let d = 0; d <= 5; d++)
    for (let w = -3; w <= 3; w++)
      for (let h = -1; h <= 2; h++) {
        const here = kindAt(d, w, h);
        if (here !== 'wall' && here !== 'ceiling') continue;
        for (const [dd, dw, dh] of STEPS) {
          const there = kindAt(d + dd, w + dw, h + dh);
          let skin: 'planks' | 'stone' | 'roof' | null = null;
          if (here === 'wall') {
            /* 🪨 Rútshellir — 🎯 haugur, shown its photo: *"make it look like
               that."* Timber lines the ROOM; the doorway and the whole outside
               are dry stone. ⚠️ #850: the outside at EVERY height — the ground
               in front can be lower than the pit floor's row, and an undrawn
               face there showed the room through the wall. Where the earth does
               stand against it, the face looks into the earth and is never seen. */
            if (dh === 0 && there === 'room') skin = 'planks';
            else if (dh === 0 && there === 'door') skin = 'stone';
            else if (dh === 1 && there === 'door') skin = 'stone';
            else if (dh === 0 && there === 'outside') skin = 'stone';
          } else {
            if (dh === -1 && (there === 'room' || there === 'door')) skin = 'planks';
            /* Around the way in: planks. Beside the door's upper half: the
               doorway's stone, on up to the turf. */
            else if (dh === 0 && there === 'room') skin = 'planks';
            else if (dh === 0 && there === 'door') skin = 'stone';
            /* 🌱 The ceiling's outside is the foot of the turf ROOF, green,
               sitting right on the stone — Rútshellir. */
            else if (dh === 0 && there === 'outside') skin = 'roof';
            else if (dh === 1 && there === 'outside') skin = 'roof';
          }
          if (skin === null) continue;
          faces.push({
            x: door.x + into.x * d + right.x * w,
            y: door.y + h,
            z: door.z + into.z * d + right.z * w,
            nx: into.x * dd + right.x * dw || 0,
            ny: dh,
            nz: into.z * dd + right.z * dw || 0,
            skin,
          });
        }
      }
  return faces;
}

/**
 * 🚪 **Where the door stands, and its turn — shut in the doorway, or open
 * against its hinge.** The hinge is the jamb on the LEFT as you face the door
 * from outside (`−right`). Open, the door turns a quarter into the room, so
 * its middle is half a door from the hinge along `into`. ⚠️ Turning it about
 * its middle (review finding 10) stood an open door across the doorway.
 */
export function doorSwing(
  centre: { readonly x: number; readonly z: number },
  facing: BurFacing,
  open: boolean,
): { x: number; z: number; turn: number } {
  const { into, right } = burFrame(facing);
  const shut = Math.atan2(into.x, into.z);
  if (!open) return { x: centre.x, z: centre.z, turn: shut };
  const hinge = { x: centre.x - right.x * 0.5, z: centre.z - right.z * 0.5 };
  /* A quarter turn lays the door's width (its local x) along `into`. */
  return { x: hinge.x + into.x * 0.5, z: hinge.z + into.z * 0.5, turn: shut - Math.PI / 2 };
}

/**
 * 🪵 **The timber gable at the front** — Rútshellir's plank triangle above the
 * stone. In the front wall's outer plane, 7 m across on the ceiling's top
 * (`h = +2`), rising 1.5 m — the roof model's height — to a point over the door.
 * Returns the two foot corners and the apex.
 */
export function pitGable(
  door: { x: number; y: number; z: number },
  facing: BurFacing,
): [
  { x: number; y: number; z: number },
  { x: number; y: number; z: number },
  { x: number; y: number; z: number },
] {
  const { into, right } = burFrame(facing);
  const cx = door.x + 0.5 - into.x * 0.5;
  const cz = door.z + 0.5 - into.z * 0.5;
  const y = door.y + 2;
  return [
    { x: cx - right.x * 3.5, y, z: cz - right.z * 3.5 },
    { x: cx + right.x * 3.5, y, z: cz + right.z * 3.5 },
    { x: cx, y: y + 1.5, z: cz },
  ];
}

/** A point in house coordinates — d along, w across, y up from the door's cell. */
function housePoint(
  door: { x: number; y: number; z: number },
  facing: BurFacing,
  d: number,
  w: number,
  y: number,
): [number, number, number] {
  const { into, right } = burFrame(facing);
  return [
    door.x + 0.5 + into.x * d + right.x * w,
    door.y + y,
    door.z + 0.5 + into.z * d + right.z * w,
  ];
}

/** 🏠 How far the roof reaches past the side walls, and past each end, in metres. */
export const ROOF_EAVE = 0.45;
export const ROOF_END = 0.3;
/** 🏠 How thick the turf is — what you see along its edges. */
export const ROOF_TURF = 0.3;

/**
 * 🏠 **A turf roof built to the house's own shape — Rútshellir.** A slab of
 * turf, `ROOF_TURF` thick, in two slopes up to a ridge running front to back,
 * its UNDERSIDE resting on the plank gable's edges (the gable's apex is the
 * ridge's underside), reaching `ROOF_EAVE` past each side wall and `ROOF_END`
 * past the gable and the back, with a turf gable at the back under it.
 * Triangles face OUT. 🌱 It wears **roof 1's grass** (`pit_roof_top.png`) —
 * 🎯 *"with roof 1's grass on it"*.
 *
 * 🎯 **haugur, 2026-10-05 (#858, #859):** *"its not connected to the gables,
 * also its to small and you can see under the roof on the sides, its should
 * come over on the sides a little bit."* The picked model, a grassy lump,
 * could never meet a straight gable.
 */
export function pitRoofGeometry(
  door: { x: number; y: number; z: number },
  facing: BurFacing,
): BufferGeometry {
  const front = -0.5 - ROOF_END;
  const back = 5.5 + ROOF_END;
  const rise = 1.5 / 3.5;
  const p = (d: number, w: number, y: number): [number, number, number] =>
    housePoint(door, facing, d, w, y);
  const { into, right } = burFrame(facing);
  const pos: number[] = [];
  const nor: number[] = [];
  const uvs: number[] = [];
  /* One triangle, wound so its normal points along `out` (house d, w, y). */
  const tri = (
    a: [number, number, number],
    b: [number, number, number],
    c: [number, number, number],
    ua: [number, number],
    ub: [number, number],
    uc: [number, number],
    out: { d: number; w: number; y: number },
  ): void => {
    const e1 = [b[0] - a[0], b[1] - a[1], b[2] - a[2]];
    const e2 = [c[0] - a[0], c[1] - a[1], c[2] - a[2]];
    let n = [
      (e1[1] ?? 0) * (e2[2] ?? 0) - (e1[2] ?? 0) * (e2[1] ?? 0),
      (e1[2] ?? 0) * (e2[0] ?? 0) - (e1[0] ?? 0) * (e2[2] ?? 0),
      (e1[0] ?? 0) * (e2[1] ?? 0) - (e1[1] ?? 0) * (e2[0] ?? 0),
    ];
    const want = [into.x * out.d + right.x * out.w, out.y, into.z * out.d + right.z * out.w];
    let verts = [a, b, c];
    let uv = [ua, ub, uc];
    if (
      (n[0] ?? 0) * (want[0] ?? 0) + (n[1] ?? 0) * (want[1] ?? 0) + (n[2] ?? 0) * (want[2] ?? 0) <
      0
    ) {
      verts = [a, c, b];
      uv = [ua, uc, ub];
      n = n.map((v) => -v);
    }
    const len = Math.hypot(n[0] ?? 0, n[1] ?? 0, n[2] ?? 0) || 1;
    for (const v of verts) pos.push(...v);
    for (let i = 0; i < 3; i++) nor.push((n[0] ?? 0) / len, (n[1] ?? 0) / len, (n[2] ?? 0) / len);
    for (const t of uv) uvs.push(...t);
  };
  type P = [number, number, number];
  type T = [number, number];
  const quad = (
    a: P,
    b: P,
    c: P,
    d: P,
    ta: T,
    tb: T,
    tc: T,
    td: T,
    out: { d: number; w: number; y: number },
  ): void => {
    tri(a, b, c, ta, tb, tc, out);
    tri(a, c, d, ta, tc, td, out);
  };
  const span = back - front;
  const eaveW = 3.5 + ROOF_EAVE;
  /* 🌱 Where a point on the roof falls in roof 1's grass, photographed from
     above (\`tools/bake-roof-top.mjs\`): across the picture with w, so each slope
     takes its own half and the halves meet at the ridge; down it with d. */
  const pic = (d: number, w: number): T => [0.5 + w / (2 * eaveW), 1 - (d - front) / span];
  /* A cut edge shows a thin strip of the grass beside it, not one smeared texel. */
  const strip = ROOF_TURF / (2 * eaveW);
  /* The underside: on the gable at the wall line, the ridge at h = +3.5. */
  const under = (w: number): number => 3.5 - Math.abs(w) * rise;
  for (const side of [-1, 1]) {
    const w = eaveW * side;
    const upOut = { d: 0, w: side * rise, y: 1 };
    const downIn = { d: 0, w: -side * rise, y: -1 };
    /* The top of the turf, and its underside. */
    for (const [lift, out] of [
      [ROOF_TURF, upOut],
      [0, downIn],
    ] as const) {
      quad(
        p(front, w, under(w) + lift),
        p(back, w, under(w) + lift),
        p(back, 0, 3.5 + lift),
        p(front, 0, 3.5 + lift),
        pic(front, w),
        pic(back, w),
        pic(back, 0),
        pic(front, 0),
        out,
      );
    }
    /* The eave's edge, along the side. */
    const inward = (t: T): T => [t[0] - side * strip, t[1]];
    quad(
      p(front, w, under(w)),
      p(back, w, under(w)),
      p(back, w, under(w) + ROOF_TURF),
      p(front, w, under(w) + ROOF_TURF),
      pic(front, w),
      pic(back, w),
      inward(pic(back, w)),
      inward(pic(front, w)),
      { d: 0, w: side, y: 0 },
    );
    /* The cut ends of the turf, front and back. */
    for (const [d, out] of [
      [front, -1],
      [back, 1],
    ] as const) {
      const back1 = (t: T): T => [t[0], t[1] + out * strip];
      quad(
        p(d, w, under(w)),
        p(d, 0, 3.5),
        p(d, 0, 3.5 + ROOF_TURF),
        p(d, w, under(w) + ROOF_TURF),
        pic(d, w),
        pic(d, 0),
        back1(pic(d, 0)),
        back1(pic(d, w)),
        { d: out, w: 0, y: 0 },
      );
    }
  }
  /* The back gable, turf, in the back wall's plane — a strip of the same grass. */
  tri(p(5.5, -3.5, 2), p(5.5, 3.5, 2), p(5.5, 0, 3.5), [0.08, 0.02], [0.92, 0.02], [0.5, 0.2], {
    d: 1,
    w: 0,
    y: 0,
  });
  const g = new BufferGeometry();
  g.setAttribute('position', new Float32BufferAttribute(pos, 3));
  g.setAttribute('normal', new Float32BufferAttribute(nor, 3));
  g.setAttribute('uv', new Float32BufferAttribute(uvs, 2));
  return g;
}

/** 🪵 The gable as a triangle, wound to face OUT of the house, boards upright. */
function gableGeometry(
  door: { x: number; y: number; z: number },
  facing: BurFacing,
): BufferGeometry {
  const { into } = burFrame(facing);
  const gable = pitGable(door, facing);
  const apex = gable[2];
  let [a, b] = [gable[0], gable[1]];
  /* Wound so (b − a) × (apex − a) points out of the front (−into). */
  const cross = (b.y - a.y) * (apex.z - a.z) - (b.z - a.z) * (apex.y - a.y);
  const crossX = cross;
  const crossZ = (b.x - a.x) * (apex.y - a.y) - (b.y - a.y) * (apex.x - a.x);
  if (crossX * -into.x + crossZ * -into.z < 0) [a, b] = [b, a];
  const g = new BufferGeometry();
  g.setAttribute(
    'position',
    new Float32BufferAttribute([a.x, a.y, a.z, b.x, b.y, b.z, apex.x, apex.y, apex.z], 3),
  );
  g.setAttribute(
    'normal',
    new Float32BufferAttribute([-into.x, 0, -into.z, -into.x, 0, -into.z, -into.x, 0, -into.z], 3),
  );
  /* The plank tile turned a quarter: its boards run UP, as on the photo. */
  g.setAttribute('uv', new Float32BufferAttribute([0, 0, 0, 7, 1.5, 3.5], 2));
  return g;
}

/** Every pit-house door (lower half) in one chunk's blocks. */
export function pitHousesIn(blocks: Uint16Array): PitAt[] {
  const out: PitAt[] = [];
  for (let i = 0; i < blocks.length; i++) {
    /* eslint-disable-next-line security/detect-object-injection --
       A loop counter reading a Uint16Array, as burmesh.ts does. */
    const id = blocks[i] ?? 0;
    if (!isPitDoor(id) || pitDoorHalf(id) !== 'lower') continue;
    out.push({
      x: i % CHUNK_SIZE,
      y: Math.floor(i / (CHUNK_SIZE * CHUNK_SIZE)),
      z: Math.floor(i / CHUNK_SIZE) % CHUNK_SIZE,
      facing: pitFacing(id) ?? 'north',
      open: pitDoorIsOpen(id),
    });
  }
  return out;
}

/** 🧱 The quads for a list of faces: positions, normals and 1 m UVs. */
export function faceGeometry(faces: readonly PitFace[]): BufferGeometry {
  const pos: number[] = [];
  const nor: number[] = [];
  const uvs: number[] = [];
  for (const f of faces) {
    /* The face's centre, then two axes across it, chosen so a × b is the
       normal: the material draws FRONT faces only, and a face wound the other
       way is invisible (a test checks every triangle). */
    const cx = f.x + 0.5 + f.nx * 0.5;
    const cy = f.y + 0.5 + f.ny * 0.5;
    const cz = f.z + 0.5 + f.nz * 0.5;
    const a: [number, number, number] =
      f.ny !== 0 ? [1, 0, 0] : f.nx !== 0 ? [0, 0, -f.nx] : [f.nz, 0, 0];
    const b: [number, number, number] = f.ny !== 0 ? [0, 0, -f.ny] : [0, 1, 0];
    const corner = (sa: number, sb: number): number[] => [
      cx + (a[0] * sa + b[0] * sb) * 0.5,
      cy + (a[1] * sa + b[1] * sb) * 0.5,
      cz + (a[2] * sa + b[2] * sb) * 0.5,
    ];
    const quad = [
      corner(-1, -1),
      corner(1, -1),
      corner(1, 1),
      corner(-1, -1),
      corner(1, 1),
      corner(-1, 1),
    ];
    /* ⚠️ v runs DOWN the tile: the tile data is top row first, and a face is
       built bottom-up, so v = 0 at the TOP corner. Upside down, the grass edge
       hung under every turf band (found in the baked picture). */
    const quv = [0, 1, 1, 1, 1, 0, 0, 1, 1, 0, 0, 0];
    for (const p of quad) pos.push(...p);
    for (let i = 0; i < 6; i++) nor.push(f.nx, f.ny, f.nz);
    uvs.push(...quv);
  }
  const g = new BufferGeometry();
  g.setAttribute('position', new Float32BufferAttribute(pos, 3));
  g.setAttribute('normal', new Float32BufferAttribute(nor, 3));
  g.setAttribute('uv', new Float32BufferAttribute(uvs, 2));
  return g;
}

/** One block face tile as its own one-layer array texture — `burmesh.ts`'s `plankTexture`. */
function tileTexture(key: string, face: string): DataArrayTexture | null {
  const plan = tilePlans().find((p) => p.key === key && p.face === face);
  if (!plan) return null;
  const size = 64;
  const tex = new DataArrayTexture(new Uint8Array(renderTile(plan, size)), size, size, 1);
  tex.colorSpace = SRGBColorSpace;
  tex.wrapS = RepeatWrapping;
  tex.wrapT = RepeatWrapping;
  tex.magFilter = NearestFilter;
  tex.minFilter = LinearMipmapLinearFilter;
  tex.generateMipmaps = true;
  tex.needsUpdate = true;
  return tex;
}

function setEmissive(material: MeshLambertNodeMaterial, node: unknown): void {
  (material as unknown as { emissiveNode: unknown }).emissiveNode = node;
}

/** 🔦 Torch light added at full, and how many steps — the houses' own numbers. */
const TORCH_LIFT = 0.5;
const TORCH_STEPS = 4;

/** 🧱 The wall materials: one per skin per torch step, built on first use. */
class WallSkins {
  private readonly made = new Map<string, MeshLambertNodeMaterial>();
  private readonly tiles = new Map<string, DataArrayTexture | null>();

  get(skin: 'planks' | 'stone' | 'roof', light: number): MeshLambertNodeMaterial {
    const step = Math.round(Math.min(1, Math.max(0, light)) * TORCH_STEPS);
    const key = `${skin}:${String(step)}`;
    const had = this.made.get(key);
    if (had) return had;
    const [tileKey, face] =
      skin === 'planks'
        ? ['rekaplanki', 'side']
        : skin === 'stone'
          ? ['grjotgardur', 'side']
          : ['gras', 'top'];
    let tex = this.tiles.get(`${tileKey}:${face}`);
    if (tex === undefined) {
      tex = tileTexture(tileKey, face);
      this.tiles.set(`${tileKey}:${face}`, tex);
    }
    const m = new MeshLambertNodeMaterial();
    if (tex) {
      const t = texture(tex, uv()).depth(int(0));
      const colour = vec3(t.r, t.g, t.b);
      m.colorNode = colour;
      setEmissive(
        m,
        colour.mul(burDaylight.mul(0.35).add(uniform((step / TORCH_STEPS) * TORCH_LIFT))),
      );
    }
    this.made.set(key, m);
    return m;
  }
}

/** 📐 Where each picked model goes and how big it is, in house-local metres. */
interface PieceFit {
  /** Centre of the piece's footprint, in house cells (d, w), and its floor height h. */
  readonly d: number;
  readonly w: number;
  readonly h: number;
  /** Size across (w), up, and along (d), in metres. */
  readonly across: number;
  readonly up: number;
  readonly along: number;
}

export const FITS = {
  /* 🔥 The oven fills the back-RIGHT corner cell, 0.9 tall — w = −2, because
     +w is the player's left as they walk in (pithouse.ts floorRole). */
  oven: { d: 4, w: -2, h: -1, across: 0.95, up: 0.9, along: 0.95 },
  /* 🧵 The bench runs the LEFT wall, four cells, half a metre. */
  bench: { d: 2.5, w: 2, h: -1, across: 1, up: 0.5, along: 4 },
  /* 🚪 The door fills the doorway, two high. */
  door: { d: 0, w: 0, h: 0, across: 1, up: 2, along: 0.12 },
} as const satisfies Record<string, PieceFit>;

type Piece = keyof typeof FITS;

/**
 * 📐 **Does this model need a quarter turn before it is sized?** — when its long
 * horizontal side runs the other way to the piece's. A model made from words
 * comes in whichever way round the generator chose; stretched as it stands, a
 * bench 4 m long in x would be squashed into a 1 × 4 that is neither (the
 * candidate sheet showed one as a pallet).
 */
export function needsQuarterTurn(
  fit: { readonly across: number; readonly along: number },
  size: { readonly x: number; readonly z: number },
): boolean {
  return fit.along > fit.across !== size.z > size.x;
}

/**
 * 🎨 **A picture on the houses' material path** — Lambert over the colour, lit
 * by the daylight uniform and a torch step, one material per step, as every
 * house model is. Shared by the picked models and the roof's grass.
 */
function pictureMaterial(
  map: Texture,
  light: number,
  made: Map<number, MeshLambertNodeMaterial>,
): MeshLambertNodeMaterial {
  const step = Math.round(Math.min(1, Math.max(0, light)) * TORCH_STEPS);
  const had = made.get(step);
  if (had) return had;
  map.colorSpace = SRGBColorSpace;
  const t = texture(map, uv());
  const colour = vec3(t.r, t.g, t.b);
  const m = new MeshLambertNodeMaterial();
  m.colorNode = colour;
  setEmissive(m, colour.mul(burDaylight.mul(0.35).add(uniform((step / TORCH_STEPS) * TORCH_LIFT))));
  made.set(step, m);
  return m;
}

/** One picked model, loaded once, re-dressed on the houses' material path. */
class PitPiece {
  private template: Object3D | null = null;
  private map: Texture | null = null;
  private readonly waiting: (() => void)[] = [];
  private readonly mats = new Map<number, MeshLambertNodeMaterial>();

  private readonly fit: PieceFit;

  constructor(url: string, fit: PieceFit) {
    this.fit = fit;
    new GLTFLoader()
      .loadAsync(url)
      .then((gltf) => {
        const scene = gltf.scene;
        scene.traverse((o) => {
          if (o instanceof Mesh && this.map === null) {
            this.map = (o.material as { map?: Texture | null }).map ?? null;
          }
        });
        /* 📐 Its long side along the piece's long side first, then sized. */
        const first = new Box3().setFromObject(scene).getSize(new Vector3());
        const turned = new Group();
        turned.add(scene);
        if (needsQuarterTurn(this.fit, first)) turned.rotation.y = Math.PI / 2;
        const box = new Box3().setFromObject(turned);
        const size = box.getSize(new Vector3());
        const centre = box.getCenter(new Vector3());
        turned.position.set(-centre.x, -box.min.y, -centre.z);
        const inner = new Group();
        inner.add(turned);
        /* Each axis to its stated size: these are objects of a size WE state. */
        inner.scale.set(
          this.fit.across / Math.max(size.x, 1e-6),
          this.fit.up / Math.max(size.y, 1e-6),
          this.fit.along / Math.max(size.z, 1e-6),
        );
        const wrap = new Group();
        wrap.add(inner);
        this.template = wrap;
        for (const fill of this.waiting.splice(0)) fill();
      })
      .catch((err: unknown) => {
        /* Not picked yet, or not shipped: the walls are drawn without it. */
        console.warn(`Pit house: ${url} did not load`, err);
      });
  }

  private materialFor(light: number): MeshLambertNodeMaterial | null {
    return this.map === null ? null : pictureMaterial(this.map, light, this.mats);
  }

  place(
    group: Group,
    house: PitAt,
    light: number,
    /** Where it stands and how it is turned, when not at its fitted spot — the door. */
    at?: { readonly x: number; readonly z: number; readonly turn: number },
  ): void {
    const fill = (): void => {
      if (this.template === null) return;
      const copy = this.template.clone(true);
      const { into, right } = burFrame(house.facing);
      const f = this.fit;
      copy.position.set(
        at?.x ?? house.x + 0.5 + into.x * f.d + right.x * f.w,
        house.y + f.h,
        at?.z ?? house.z + 0.5 + into.z * f.d + right.z * f.w,
      );
      copy.rotation.y = at?.turn ?? Math.atan2(into.x, into.z);
      const mat = this.materialFor(light);
      if (mat !== null) {
        copy.traverse((o) => {
          if (o instanceof Mesh) o.material = mat;
        });
      }
      group.add(copy);
    };
    if (this.template !== null) fill();
    else this.waiting.push(fill);
  }
}

/** 🏠 The four picked models, loaded once and shared by every pit house. */
export class PitHouseModels {
  private readonly pieces: Record<Piece, PitPiece>;
  private readonly skins = new WallSkins();
  /** 🌱 Roof 1's grass, photographed from above — the roof wears it (#858, #859). */
  private readonly roofGrass: Texture;
  private readonly roofMats = new Map<number, MeshLambertNodeMaterial>();

  constructor(urlFor: (piece: Piece) => string, roofPicture: string) {
    /* Drawn at once; the loader fills the picture in when it arrives. */
    this.roofGrass = new TextureLoader().load(roofPicture, undefined, undefined, (err: unknown) => {
      console.warn(`Pit house: ${roofPicture} did not load`, err);
    });
    this.pieces = {
      oven: new PitPiece(urlFor('oven'), FITS.oven),
      bench: new PitPiece(urlFor('bench'), FITS.bench),
      door: new PitPiece(urlFor('door'), FITS.door),
    };
  }

  /** Everything one pit house draws, into `group` at the chunk's origin. */
  place(group: Group, house: PitAt, litAt: (x: number, y: number, z: number) => number): void {
    const { into, right } = burFrame(house.facing);
    const cell = (d: number, w: number, h: number): [number, number, number] => [
      house.x + into.x * d + right.x * w,
      house.y + h,
      house.z + into.z * d + right.z * w,
    ];
    /* 🔦 The room's light from its middle, the weather side's from outside the door. */
    const inside = litAt(...cell(2, 0, 0));
    const outside = litAt(...cell(-1, 0, 0));

    const faces = pitWallFaces(house, house.facing);
    const planks = faces.filter((f) => f.skin === 'planks');
    const stone = faces.filter((f) => f.skin === 'stone');
    const roof = faces.filter((f) => f.skin === 'roof');
    for (const [geometry, mat] of [
      [faceGeometry(planks), this.skins.get('planks', inside)],
      [faceGeometry(stone), this.skins.get('stone', outside)],
      [faceGeometry(roof), this.skins.get('roof', outside)],
      /* 🪵 The timber gable at the front, in the outside's light. */
      [gableGeometry(house, house.facing), this.skins.get('planks', outside)],
    ] as const) {
      const mesh = new Mesh(geometry, mat);
      /* 🧹 This geometry belongs to THIS house — `disposePitHouseGroup` frees it. */
      mesh.userData['ownGeometry'] = true;
      group.add(mesh);
    }

    const roofMesh = new Mesh(
      pitRoofGeometry(house, house.facing),
      pictureMaterial(this.roofGrass, outside, this.roofMats),
    );
    roofMesh.userData['ownGeometry'] = true;
    group.add(roofMesh);
    this.pieces.oven.place(group, house, inside);
    this.pieces.bench.place(group, house, inside);
    /* 🚪 Open, the door turns a quarter on its HINGE — into the room. */
    this.pieces.door.place(
      group,
      house,
      outside,
      doorSwing({ x: house.x + 0.5, z: house.z + 0.5 }, house.facing, house.open),
    );
  }
}

/** One group per chunk, or `null` when the chunk has no pit house in it. */
export function buildPitHouseGroup(
  houses: readonly PitAt[],
  models: PitHouseModels,
  litAt: (x: number, y: number, z: number) => number = () => 0,
): Group | null {
  if (houses.length === 0) return null;
  const group = new Group();
  for (const h of houses) models.place(group, h, litAt);
  return group;
}

/**
 * 🧹 **Free a chunk's pit-house walls.** Their geometry is built per house, so
 * unlike a shared model template it must be disposed when the chunk goes, or
 * walking about leaks memory. Materials and model templates are shared — kept.
 */
export function disposePitHouseGroup(group: Group): void {
  group.traverse((o) => {
    if (o instanceof Mesh && o.userData['ownGeometry'] === true)
      (o.geometry as BufferGeometry).dispose();
  });
}
